/* Phase 6 · Refresh runbook — live status of the ordered refresh steps */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, AlertCircle, RefreshCw } from "lucide-react";

const toneMap = {
  ok:   { icon: CheckCircle2, cls: "text-emerald-600", ring: "ring-emerald-100" },
  warn: { icon: AlertCircle,  cls: "text-amber-600",   ring: "ring-amber-100" },
  todo: { icon: Circle,       cls: "text-slate-400",   ring: "ring-slate-100" },
};

export default function RefreshChecklist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/refresh-checklist");
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-6 text-sm text-[#7C8E9E]">Loading…</div>;

  return (
    <div className="p-4 md:p-6 max-w-3xl" data-testid="refresh-checklist">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">ADMIN · REFRESH RUNBOOK</div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0B2340]">Catalogue refresh checklist</h1>
          <p className="text-xs text-[#4A627A] mt-1">
            Follow these steps in order. Live status against the current database.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="refresh-reload"
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-slate-200">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh status
        </button>
      </div>

      <ol className="space-y-3">
        {data.steps.map(s => {
          const T = toneMap[s.status] || toneMap.todo;
          const Icon = T.icon;
          return (
            <li key={s.n} data-testid={`refresh-step-${s.n}`}
              className={`bg-white rounded-lg border border-slate-100 p-4 flex items-start gap-3 ring-4 ${T.ring}`}>
              <div className={`w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 ${T.cls}`}>
                <Icon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono text-[#7C8E9E]">STEP {s.n}</span>
                  <span className="font-semibold text-[#0B2340]">{s.title}</span>
                </div>
                <div className="text-xs text-[#4A627A] mt-1">{s.detail}</div>
                {s.n === 1 && s.status !== "ok" && (
                  <Link to="/admin/maintenance" className="mt-2 inline-block text-[11px] font-semibold text-[#B08B49] underline"
                    data-testid="refresh-goto-snapshot">Go to Maintenance → Export snapshot</Link>
                )}
                {(s.n === 2 || s.n === 3 || s.n === 4) && (
                  <Link to="/admin/csv-upload" className="mt-2 inline-block text-[11px] font-semibold text-[#B08B49] underline">
                    Open CSV Upload →
                  </Link>
                )}
                {s.n === 5 && (
                  <Link to="/director/import" className="mt-2 inline-block text-[11px] font-semibold text-[#B08B49] underline">
                    Open Data Import (Handwoven / Quality / LSD) →
                  </Link>
                )}
              </div>
              <span className={`text-[10px] uppercase font-bold ${T.cls} flex-shrink-0`}>{s.status}</span>
            </li>
          );
        })}
      </ol>

      <div className="mt-4 text-[10px] text-[#7C8E9E] text-right">
        Generated at {data.generated_at?.slice(11, 19)} UTC
      </div>
    </div>
  );
}
